<template>
  <div id="app">
    <!-- Beta 环境水印 -->
    <van-watermark
      v-if="isBeta"
      content="Driver Beta"
      :full-page="true"
      :opacity="0.5"
      :gap-x="80"
      :gap-y="20"
      :z-index="9999999"
    />
    <div id="app" v-if="hasLocationPermission">
      <router-view v-slot="{ Component }">
        <transition :name="transitionName" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </div>
    <div v-else class="no-permission">
      <van-empty description="请开启定位权限后重新进入APP" />
    </div>
  </div>
</template>

<script>
import { showDialog, showConfirmDialog  } from 'vant';

export default {
  name: "App",
  data() {
    return {
      isBeta: import.meta.env.MODE === 'beta', // 判断是否为 Beta 环境
      hasLocationPermission: true, // 是否拥有定位权限
      permissionWatcher: null, // 权限监控对象
      currentLocation: {
        latitude: null,
        longitude: null,
      },
    };
  },
  computed: {
    // 动态获取路由的过渡动画名称
    transitionName() {
      return this.$route.meta.transition || "fade"; // 如果没有定义，默认为 'fade'
    },
  },
  async mounted() {
    this.checkWeChatBrowser();
    await google.maps.importLibrary("maps");
    await this.checkLocationPermission(); // 检查定位权限
    this.startPermissionWatcher(); // 开启权限监控
    this.startGpsTracking();
  },
  beforeDestroy() {
    this.stopPermissionWatcher(); // 停止权限监控
  },
  methods: {
    checkWeChatBrowser() {
      const ua = navigator.userAgent.toLowerCase();
      const isWeChatBrowser = ua.indexOf('micromessenger') !== -1;
      if (isWeChatBrowser) {
        this.showWeChatTip();
      }
    },
    showWeChatTip() {
      showDialog({
        title: '请使用默认浏览器访问',
        message: '请点击右上角 ···，选择【在浏览器中打开】以获得更好的体验。',
        showConfirmButton: false,
        closeOnClickOverlay: false,
      });
    },
    async checkLocationPermission() {
      try {
        const permissionStatus = await navigator.permissions.query({ name: "geolocation" });

        // **如果权限是 `granted`，直接启用 GPS，不弹窗**
        if (permissionStatus.state === "granted" || permissionStatus.state === "prompt") {
          this.hasLocationPermission = true;
          console.log("✅ 定位权限已授权，直接获取位置");
          return;
        }

        const dialogInstance = showConfirmDialog({
          title: "开启定位权限",
          message: "📍 请允许访问你的位置信息，以便获取最新订单。",
          showConfirmButton: false,
          showCancelButton: false,
        });

        // 3秒后调用定位权限请求
        setTimeout(async () => {
          const permission = await this.requestLocationPermission();
          if (permission === "granted") {
            console.log("✅ 用户允许定位权限");
            this.hasLocationPermission = true;
            this.startGpsTracking();
          } else {
            console.log("❌ 用户拒绝了定位权限");
            this.hasLocationPermission = false;
            // 如有需要，也可以做相应提示处理
          }
          // 授权结果出来后关闭提示栏
          dialogInstance.close();
        }, 3000);

        // 监听权限变化，自动更新状态
        permissionStatus.onchange = () => {
          this.updatePermissionStatus(permissionStatus.state);
        };
      } catch (error) {
        console.error("无法检查定位权限:", error);
        this.hasLocationPermission = false;
      }
    },
    async requestLocationPermission() {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve("granted"),
          () => resolve("denied"),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
    },
    updatePermissionStatus(state) {
      if (state === "granted" || state === "prompt") {
        this.hasLocationPermission = true;
        console.log("✅ 位置权限已授予");
      } else {
        this.hasLocationPermission = false;
        console.log("❌ 位置权限被拒绝");
      }
    },
    startGpsTracking() {
      if (this.hasLocationPermission) {
        this.updateLocation();
        this.GpsIntervalId = setInterval(() => {
          this.updateLocation();
        }, 10000);
      } else {
        console.log("⏳ 等待用户开启 GPS 权限");
      }
    },
    async updateLocation() {
      const latLng = await this.getCurrentLocation();
      if (latLng) {
        await this.reportLocation(latLng);
      }
    },
    async getCurrentLocation() {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        });

        return {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
      } catch (error) {
        console.error("❌ 获取位置信息失败:", error);
        return null;
      }
    },
    async reportLocation(latLng) {
      try {
        if (!latLng) return;

        const response = await this.$driverAxios.post("/api/driver/updateGpsSync", {
          latitude: latLng.lat,
          longitude: latLng.lng,
        });

        if (response.data.code === 200) {
          console.log("✅ 位置上报成功:", response.data.message);
        } else {
          console.error("❌ 位置上报失败:", response.data.message);
        }
      } catch (error) {
        console.error("❌ 位置上报错误:", error);
      }
    },
    startPermissionWatcher() {
      this.permissionWatcher = setInterval(() => {
        navigator.permissions
          .query({ name: "geolocation" })
          .then((permissionStatus) => {
            console.log(permissionStatus)
            console.log(permissionStatus.state)
            this.updatePermissionStatus(permissionStatus.state);
          });
      }, 5000);
    },

    stopPermissionWatcher() {
      if (this.permissionWatcher) {
        clearInterval(this.permissionWatcher);
        this.permissionWatcher = null;
      }
    },
  },
};
</script>

<style>
/* 默认过渡 */
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }

  /* 左过渡 */
  .slide-left-enter-active, .slide-left-leave-active {
    transition: transform 0.3s;
  }
  .slide-left-enter-from {
    transform: translateX(100%);
  }
  .slide-left-leave-to {
    transform: translateX(-100%);
  }

  /* 右过渡 */
  .slide-right-enter-active, .slide-right-leave-active {
    transition: transform 0.3s;
  }
  .slide-right-enter-from {
    transform: translateX(-100%);
  }
  .slide-right-leave-to {
    transform: translateX(100%);
  }

  .no-permission {
    margin-top: 150px;
  }
</style>
